import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import { useTranslation } from 'react-i18next';

import { OpenableSession } from '../../../utils/activeSessions';

interface ActiveSessionsSheetProps {
  open: boolean;
  sessions: OpenableSession[];
  onSelect: (session: OpenableSession) => void;
  onClose: () => void;
}

/**
 * Which charge to look at, when the account has more than one running. The
 * bolt tab opens this instead of guessing at one of them.
 */
export default function ActiveSessionsSheet({
  open,
  sessions,
  onSelect,
  onClose,
}: ActiveSessionsSheetProps) {
  const { t } = useTranslation();

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      // The app is dark-themed through its own tokens rather than a MUI dark
      // palette, so a Drawer left alone paints MUI's white paper and near-black
      // text — a white sheet over a black app.
      PaperProps={{
        sx: {
          backgroundColor: 'gray.300',
          backgroundImage: 'none',
          color: 'gray.main',
          maxWidth: '400px',
          margin: '0 auto',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
        },
      }}
    >
      <Stack sx={{ padding: '16px 16px 24px' }}>
        <Typography variant="h6" sx={{ marginBottom: '8px', color: 'gray.main' }}>
          {t('activeSessionsSheet.title')}
        </Typography>
        <List sx={{ padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {sessions.map((session) => (
            <ListItemButton
              key={`${session.stationId}-${session.connectorId}`}
              onClick={() => onSelect(session)}
              sx={{
                borderRadius: '12px',
                backgroundColor: 'gray.200',
                '&:hover': { backgroundColor: 'gray.150' },
              }}
            >
              <BoltIcon sx={{ color: 'green.main', marginRight: '12px' }} />
              <ListItemText
                sx={{
                  '& .MuiListItemText-primary': { color: 'gray.main' },
                  '& .MuiListItemText-secondary': { color: 'gray.100' },
                }}
                primary={
                  session.stationAddress ??
                  t('activeSessionsSheet.connector', { id: session.connectorId })
                }
                secondary={[
                  `${session.percent}%`,
                  session.powerKw !== undefined
                    ? t('activeSessionsSheet.power', { value: session.powerKw })
                    : null,
                  session.stationAddress
                    ? t('activeSessionsSheet.connector', { id: session.connectorId })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </ListItemButton>
          ))}
        </List>
      </Stack>
    </Drawer>
  );
}
